/**
 * Central Hero / R3F configuration.
 * Feature flags stay off until each interaction is intentionally implemented.
 * Tuning lives here so scene components stay declarative.
 */

export const HERO_FEATURES = {
  mouseTracking: false,
  neckRotation: false,
  eyeTracking: false,
  jumpAnimation: false,
  idleBreathing: false,
  blink: false,
  tailWag: false,
  headTilt: false,
  clickInteraction: false,
  hoverInteraction: false,
  scrollInteraction: false,
  cameraMovement: false,
  soundEffects: false,
  ctaAwareness: false,
  randomIdle: false,
  animationBlending: false,
  postprocessing: false,
  particles: false,
};

export const heroConfig = {
  features: HERO_FEATURES,

  camera: {
    position: [0.35, 1.15, 4.2],
    lookAt: [0, 0.55, 0],
    fov: 32,
    near: 0.1,
    far: 80,
    /** Responsive overrides (width breakpoints in px) */
    responsive: {
      mobile: { position: [0.15, 1.35, 5.2], fov: 38 },
      tablet: { position: [0.25, 1.25, 4.6], fov: 34 },
    },
    /** Future offset channels (mouse / scroll / idle / focus) — consumed by CameraRig */
    channels: {
      mouse: { enabled: false, strength: 0.12 },
      scroll: { enabled: false, strength: 0.35 },
      idle: { enabled: false, amplitude: 0.04, speed: 0.35 },
      focus: { enabled: false },
    },
    lerp: 0.08,
  },

  lighting: {
    ambient: { intensity: 0.22, color: "#f5f0e8" },
    hemisphere: {
      sky: "#e8e4dc",
      ground: "#1a1814",
      intensity: 0.45,
    },
    key: {
      position: [3.2, 4.5, 2.8],
      intensity: 1.35,
      color: "#ffe4c4",
      castShadow: true,
      shadowMapSize: 1024,
      shadowBias: -0.0002,
      shadowNormalBias: 0.02,
      shadowCamera: { near: 0.5, far: 18, size: 4 },
    },
    rim: {
      position: [-3.5, 2.2, -2.5],
      intensity: 0.85,
      color: "#9bb8d4",
    },
    fill: {
      position: [-1.5, 1.8, 3.5],
      intensity: 0.35,
      color: "#fff8f0",
    },
  },

  environment: {
    /** Soft studio-like clear color under CSS photo / gradient overlays */
    background: null,
    fog: { enabled: true, color: "#050505", near: 8, far: 22 },
    /** drei Environment preset reserved for future; off in foundation */
    preset: null,
  },

  ground: {
    visible: true,
    size: 12,
    y: -0.01,
    color: "#0a0a0a",
    roughness: 0.92,
    metalness: 0.05,
    receiveShadow: true,
  },

  /** Slot transform for the future puppy model */
  puppySlot: {
    position: [0, 0, 0],
    rotation: [0, 0.35, 0],
    scale: 1,
  },

  renderer: {
    antialias: true,
    alpha: true,
    powerPreference: "high-performance",
    toneMapping: "ACESFilmic",
    toneMappingExposure: 1.05,
    outputColorSpace: "srgb",
    shadows: true,
    shadowType: "PCFSoft",
  },

  performance: {
    /** [min, max] device pixel ratio */
    dpr: {
      high: [1, 1.75],
      medium: [1, 1.25],
      low: [1, 1],
    },
    /** detect-gpu fps thresholds → tier labels */
    gpu: {
      highMinFps: 50,
      mediumMinFps: 30,
    },
    frameloopWhenVisible: "demand",
    frameloopWhenHidden: "never",
    pauseWhenOffscreen: true,
    enableShadowsAboveTier: "medium",
  },

  mouse: {
    /** Existing CSS bg parallax (DOM) — separate from 3D tracking */
    cssParallax: { strength: 24, duration: 1.2 },
    /** Future 3D normalized pointer (-1…1) */
    smoothing: 0.12,
    deadzone: 0.02,
  },
};

export default heroConfig;
